import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ShieldCheck, ShieldAlert, ShieldX, Users } from "lucide-react";

export default function RenterInsurancePage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [residents, setResidents] = useState([]);
  const [form, setForm] = useState({
    resident_id: "", carrier: "", policy_number: "",
    coverage_amount: 10000, liability_coverage: 100000,
    effective_date: new Date().toISOString().slice(0, 10),
    expiration_date: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
    certificate_url: "", additional_insured_named: false, notes: "",
  });

  const load = async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/renter-insurance?property_id=${propertyId}`);
      setItems(data.items || []);
      setStats(data.stats || null);
      const { data: r } = await api.get(`/admin/users?role=resident&property_id=${propertyId}`);
      setResidents(r.items || []);
    } catch (e) { toast.error("Failed to load"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [propertyId]);

  const create = async () => {
    if (!form.resident_id || !form.carrier || !form.policy_number) {
      toast.error("Resident, carrier, and policy number required");
      return;
    }
    try {
      await api.post("/admin/renter-insurance", {
        property_id: propertyId,
        ...form,
        coverage_amount: parseFloat(form.coverage_amount),
        liability_coverage: parseFloat(form.liability_coverage),
      });
      toast.success("Policy added");
      setDialog(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const syncMissing = async () => {
    try {
      const { data } = await api.post(`/admin/renter-insurance/sync-missing?property_id=${propertyId}`);
      toast.success(`Scanned ${data.residents_scanned}, added ${data.placeholders_created} missing placeholders`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const verify = async (pid) => {
    try {
      await api.patch(`/admin/renter-insurance/${pid}`, { verified_by_admin: true });
      toast.success("Verified");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const statusBadge = (s) => {
    if (s === "active") return <Badge className="bg-emerald-600"><ShieldCheck size={10} className="mr-1" />Active</Badge>;
    if (s === "expiring_soon") return <Badge className="bg-amber-500"><ShieldAlert size={10} className="mr-1" />Expiring</Badge>;
    if (s === "expired") return <Badge variant="destructive"><ShieldX size={10} className="mr-1" />Expired</Badge>;
    return <Badge variant="secondary"><ShieldX size={10} className="mr-1" />Missing</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="renter-insurance-page">
      <PageHeader
        eyebrow="Compliance"
        title="Renters Insurance"
        subtitle="Every resident should carry renters insurance. Insurers often give HOA/COA discounts for 100% compliance."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={syncMissing} data-testid="sync-missing-btn">Sync missing</Button>
            <Button onClick={() => setDialog(true)} className="gap-2" data-testid="new-policy-btn">
              <Plus size={14} /> Add policy
            </Button>
          </div>
        }
      />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Users size={12} /> Residents
            </div>
            <div className="text-2xl font-semibold mt-1" data-testid="stat-residents">{stats.total_residents}</div>
          </Card>
          <Card className="p-4 border-emerald-500/30 bg-emerald-500/5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Active</div>
            <div className="text-2xl font-semibold mt-1 text-emerald-500" data-testid="stat-active">{stats.active}</div>
          </Card>
          <Card className="p-4 border-amber-500/30 bg-amber-500/5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Expiring &lt;30d</div>
            <div className="text-2xl font-semibold mt-1 text-amber-500" data-testid="stat-expiring">{stats.expiring_soon}</div>
          </Card>
          <Card className="p-4 border-rose-500/30 bg-rose-500/5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Expired</div>
            <div className="text-2xl font-semibold mt-1 text-rose-400" data-testid="stat-expired">{stats.expired}</div>
          </Card>
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Compliance</div>
            <div className={`text-2xl font-semibold mt-1 ${stats.compliance_pct >= 90 ? "text-emerald-500" : stats.compliance_pct >= 60 ? "text-amber-500" : "text-rose-400"}`} data-testid="stat-compliance">
              {stats.compliance_pct !== null ? `${stats.compliance_pct}%` : "—"}
            </div>
          </Card>
        </div>
      )}

      <Card className="border-white/10 bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Resident</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead>Policy #</TableHead>
              <TableHead className="text-right">Coverage</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow className="border-white/5"><TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm" data-testid="policies-empty">
                No policies yet. Click <em>Sync missing</em> to auto-generate placeholders for every resident.
              </TableCell></TableRow>
            )}
            {items.map((p) => (
              <TableRow key={p.id} className="border-white/5" data-testid={`policy-${p.id}`}>
                <TableCell className="font-medium">{p.resident_name}</TableCell>
                <TableCell>{p.unit || "—"}</TableCell>
                <TableCell className="text-sm">{p.carrier || <span className="text-muted-foreground italic">—</span>}</TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground">{p.policy_number || "—"}</TableCell>
                <TableCell className="text-right font-mono text-sm">${(p.coverage_amount || 0).toLocaleString()}</TableCell>
                <TableCell className="text-sm">
                  {p.expiration_date || "—"}
                  {p.days_to_expiry !== null && p.days_to_expiry >= 0 && p.days_to_expiry <= 30 && (
                    <div className="text-[10px] text-amber-500">{p.days_to_expiry}d left</div>
                  )}
                </TableCell>
                <TableCell>{statusBadge(p.status)}</TableCell>
                <TableCell className="text-right">
                  {!p.verified_by_admin && p.status === "active" && (
                    <Button size="sm" variant="outline" onClick={() => verify(p.id)} data-testid={`verify-${p.id}`}>Verify</Button>
                  )}
                  {p.verified_by_admin && <Badge variant="outline" className="text-[10px]">Verified</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg" data-testid="policy-dialog">
          <DialogHeader><DialogTitle>Add renter policy</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2 col-span-2"><Label>Resident</Label>
              <Select value={form.resident_id} onValueChange={(v) => setForm({ ...form, resident_id: v })}>
                <SelectTrigger data-testid="pol-resident"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · Unit {r.unit || "—"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Carrier</Label>
              <Input placeholder="Lemonade / State Farm / GEICO" value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} data-testid="pol-carrier" /></div>
            <div className="space-y-2"><Label>Policy #</Label>
              <Input value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} data-testid="pol-num" /></div>
            <div className="space-y-2"><Label>Coverage $</Label>
              <Input type="number" value={form.coverage_amount} onChange={(e) => setForm({ ...form, coverage_amount: e.target.value })} data-testid="pol-cov" /></div>
            <div className="space-y-2"><Label>Liability $</Label>
              <Input type="number" value={form.liability_coverage} onChange={(e) => setForm({ ...form, liability_coverage: e.target.value })} data-testid="pol-liab" /></div>
            <div className="space-y-2"><Label>Effective</Label>
              <Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="pol-eff" /></div>
            <div className="space-y-2"><Label>Expires</Label>
              <Input type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} data-testid="pol-exp" /></div>
            <div className="space-y-2 col-span-2"><Label>Certificate URL (S3 or link)</Label>
              <Input value={form.certificate_url} onChange={(e) => setForm({ ...form, certificate_url: e.target.value })} data-testid="pol-cert" /></div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={form.additional_insured_named} onChange={(e) => setForm({ ...form, additional_insured_named: e.target.checked })} data-testid="pol-ai" />
              <Label>HOA named as additional insured</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={create} data-testid="pol-save">Add policy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
